import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, ArrowRight } from "lucide-react";
import { useAdminAuth } from "../AdminAuthContext";
import { adminLogin } from "../adminApi";
import { Logo } from "../../components/shared/Logo";
import { Button } from "../../components/shared/Button";

export default function AdminLogin() {
    const navigate = useNavigate();
    const location = useLocation();
    const { setSession, token } = useAdminAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const redirectTo =
        new URLSearchParams(location.search).get("next") || "/admin/dashboard";

    if (token) {
        navigate(redirectTo, { replace: true });
        return null;
    }

    async function onSubmit(e) {
        e?.preventDefault?.();
        setError(null);
        setSubmitting(true);
        const res = await adminLogin({ email: email.trim(), password });
        setSubmitting(false);
        if (res.ok) {
            setSession(res.data.token, res.data.email);
            navigate(redirectTo, { replace: true });
            return;
        }
        if (res.rateLimited) {
            setError("Too many attempts. Wait a few minutes and try again.");
            return;
        }
        setError(res.error || "Sign in failed.");
    }

    return (
        <div className="relative min-h-screen klareet-grain klareet-glow grid place-items-center px-5">
            <div className="w-full max-w-md">
                <div className="text-center">
                    <Logo className="h-14 mx-auto" />
                    <h1
                        className="mt-8 text-3xl md:text-4xl font-semibold tracking-tight"
                        data-testid="admin-login-headline"
                    >
                        Admin sign-in
                    </h1>
                    <p className="mt-2 text-klareet-MUT text-sm">
                        Survey operations dashboard
                    </p>
                </div>

                <form
                    onSubmit={onSubmit}
                    className="mt-8 rounded-3xl border border-klareet-bor bg-klareet-card p-6 md:p-8 space-y-4"
                    data-testid="admin-login-form"
                >
                    <Field
                        label="Email"
                        icon={<Mail size={16} className="text-klareet-MUT" />}
                    >
                        <input
                            type="email"
                            autoComplete="email"
                            inputMode="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@klareet.com"
                            required
                            className="w-full bg-transparent outline-none text-klareet-TEXT placeholder:text-klareet-DIM"
                            data-testid="admin-login-email"
                        />
                    </Field>
                    <Field
                        label="Password"
                        icon={<Lock size={16} className="text-klareet-MUT" />}
                    >
                        <input
                            type={showPw ? "text" : "password"}
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="flex-1 bg-transparent outline-none text-klareet-TEXT placeholder:text-klareet-DIM"
                            data-testid="admin-login-password"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPw((s) => !s)}
                            className="ml-2 text-klareet-MUT hover:text-klareet-T2 transition-colors"
                            aria-label={showPw ? "Hide password" : "Show password"}
                            data-testid="show-password-toggle"
                        >
                            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </Field>

                    {error ? (
                        <div
                            className="text-sm text-klareet-RED font-mono"
                            data-testid="admin-login-error"
                        >
                            {error}
                        </div>
                    ) : null}

                    <Button
                        size="lg"
                        onClick={onSubmit}
                        disabled={submitting}
                        className="w-full"
                        data-testid="admin-login-submit"
                    >
                        {submitting ? "Signing in…" : "Sign in"}
                        {!submitting && <ArrowRight size={16} />}
                    </Button>
                </form>

                <div className="mt-6 text-center text-xs text-klareet-DIM">
                    Authorized personnel only ·{" "}
                    <a
                        href="mailto:klair@klareet.com"
                        className="text-klareet-T2 hover:text-klareet-TEXT underline-offset-4 hover:underline"
                    >
                        klair@klareet.com
                    </a>
                </div>
            </div>
        </div>
    );
}

function Field({ label, icon, children }) {
    return (
        <label className="block">
            <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-klareet-MUT mb-1">
                {label}
            </div>
            <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-klareet-bor bg-klareet-NAVY focus-within:border-klareet-T focus-within:ring-2 focus-within:ring-klareet-T/40 transition-colors">
                {icon}
                {children}
            </div>
        </label>
    );
}
